import { CacheClient } from "@/manager/cache-client/CacheClient";
import { CacheKey, CacheSchemaType } from "@/modules/cache/constants";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";

export type CacheGetOptions = Parameters<ReturnType<CacheClient["get"]>>[1];
export type ScopeIdResolver = () => string | null | Promise<string | null>;

export type CacheStoreConfig = {
  databaseId: string;
  scopeResolver?: ScopeIdResolver;
};

const DEFAULT_GLOBAL_SCOPE_ID = "global";
const DEFAULT_SCOPE_RESOLVER: ScopeIdResolver = async () => DEFAULT_GLOBAL_SCOPE_ID;

export class CacheStore {
  private readonly cacheClient: CacheClient;
  private readonly scopeResolver: ScopeIdResolver;
  private readonly databaseId: string;
  private initializePromise: Promise<void> | null = null;

  constructor({ databaseId, scopeResolver }: CacheStoreConfig) {
    const resolver = scopeResolver ?? DEFAULT_SCOPE_RESOLVER;

    this.databaseId = databaseId;
    this.cacheClient = new CacheClient({ databaseId });
    this.scopeResolver = resolver;
  }

  async initialize() {
    if (!this.initializePromise) {
      this.initializePromise = this.cacheClient.initialize().catch(error => {
        this.initializePromise = null;
        throw error;
      });
    }

    await this.initializePromise;
  }

  private async resolveScopeId(): Promise<string | null> {
    try {
      return await this.scopeResolver();
    } catch (unknownErr) {
      const err = unknownErr as Error;
      logger.error({
        message: `[${this.databaseId}] Failed to resolve scope id`,
        info: { error: objectModule.safeErrorAsJson(err) },
      });
      return null;
    }
  }

  private async ensureScope(): Promise<string | null> {
    await this.initialize();
    const scopeId = await this.resolveScopeId();

    if (!scopeId) {
      logger.warn({ message: `[${this.databaseId}] No scope id available` });
      return null;
    }

    return scopeId;
  }

  async get<TCacheKey extends CacheKey>(
    cacheKey: TCacheKey,
    options?: CacheGetOptions
  ): Promise<CacheSchemaType[TCacheKey] | null> {
    const scopeId = await this.ensureScope();
    if (!scopeId) {
      return null;
    }

    const result = await this.cacheClient.get(scopeId)(cacheKey, options);

    if (result !== null) {
      return result;
    }

    return null;
  }

  async set<TCacheKey extends CacheKey>(
    cacheKey: TCacheKey,
    payload: CacheSchemaType[TCacheKey]
  ): Promise<boolean> {
    const scopeId = await this.ensureScope();
    if (!scopeId) {
      return false;
    }

    const success = await this.cacheClient.set(scopeId)(cacheKey, payload);

    return success;
  }

  async del<TCacheKey extends CacheKey>(cacheKey: TCacheKey): Promise<boolean> {
    const scopeId = await this.ensureScope();
    if (!scopeId) {
      return false;
    }

    const success = await this.cacheClient.del(scopeId)(cacheKey);

    return success;
  }

  async clearAll(): Promise<boolean> {
    await this.initialize();
    return this.cacheClient.clearAll();
  }
}
